import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { getQuestion, createQuestion, updateQuestion, getCategories } from '../../lib/adminApi';

function AdminQuestionForm() {
  const navigate = useNavigate();
  const { id } = useParams();
  const isEditMode = Boolean(id);

  const [loading, setLoading] = useState(isEditMode);
  const [saving, setSaving] = useState(false);
  const [categories, setCategories] = useState([]);
  const [error, setError] = useState(null);

  const [formData, setFormData] = useState({
    question: '',
    categoryId: '',
    template: '',
    answer: '',
    statements: '',
    connectionScopes: '',
  });

  const [testCases, setTestCases] = useState([]);

  // Fetch categories on mount
  useEffect(() => {
    const loadCategories = async () => {
      try {
        const data = await getCategories();
        setCategories(data.categories || []);
      } catch (err) {
        console.error('Failed to load categories:', err);
      }
    };
    loadCategories();
  }, []);

  // Fetch question data if in edit mode
  useEffect(() => {
    if (isEditMode) {
      const loadQuestion = async () => {
        try {
          const data = await getQuestion(id);
          if (data) {
            setFormData({
              question: data.question || '',
              categoryId: data.categoryId || '',
              template: data.template || '',
              answer: data.answer || '',
              statements: data.statements || '',
              connectionScopes: data.connectionScopes || '',
            });
            setTestCases(data.testCases || []);
          }
        } catch (err) {
          setError('Failed to load question');
          console.error('Failed to load question:', err);
        } finally {
          setLoading(false);
        }
      };
      loadQuestion();
    }
  }, [id, isEditMode]);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleTestCaseChange = (index, field, value) => {
    setTestCases(prev => {
      const updated = [...prev];
      updated[index] = { ...updated[index], [field]: value };
      return updated;
    });
  };

  const addTestCase = () => {
    setTestCases(prev => [...prev, {
      inputValue: '',
      outputType: 'string',
      outputValue: '',
      hidden: false,
      explanation: '',
    }]);
  };

  const removeTestCase = (index) => {
    setTestCases(prev => prev.filter((_, i) => i !== index));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);
    setError(null);

    try {
      const payload = {
        ...formData,
        // IDs are opaque strings — parseInt would corrupt 17-digit ROWIDs
        categoryId: formData.categoryId,
        testCases: testCases.map(tc => ({
          ...(tc.testCaseId ? { testCaseId: tc.testCaseId } : {}),
          inputValue: tc.inputValue || '',
          outputType: tc.outputType || 'string',
          outputValue: tc.outputValue || '',
          hidden: tc.hidden || false,
          explanation: tc.explanation || '',
        })),
      };

      if (isEditMode) {
        await updateQuestion(id, payload);
      } else {
        await createQuestion(payload);
      }

      navigate('/admin/questions');
    } catch (err) {
      setError(err.message || 'Failed to save question');
      console.error('Failed to save question:', err);
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="w-8 h-8 border-4 border-blue-500 border-t-transparent rounded-full animate-spin"></div>
      </div>
    );
  }

  return (
    <div>
      <div className="flex justify-between items-center mb-6">
        <h1 className="text-2xl font-bold text-white">
          {isEditMode ? 'Edit Question' : 'Add New Question'}
        </h1>
        <button
          onClick={() => navigate('/admin/questions')}
          className="px-4 py-2 bg-gray-600 text-white rounded-lg hover:bg-gray-500"
        >
          Back to Questions
        </button>
      </div>

      {error && (
        <div className="mb-4 p-4 bg-red-600/20 border border-red-500/30 rounded-lg text-red-400">
          {error}
        </div>
      )}

      <form onSubmit={handleSubmit} className="space-y-6">
        {/* Question Text */}
        <div className="bg-gray-800 rounded-lg border border-gray-700 p-6">
          <h2 className="text-lg font-semibold text-white mb-4">Question Details</h2>

          <div className="space-y-4">
            <div>
              <label className="block text-gray-400 text-sm mb-1">Question Text *</label>
              <textarea
                name="question"
                value={formData.question}
                onChange={handleInputChange}
                required
                rows={3}
                className="w-full px-4 py-2 bg-gray-700 text-white border border-gray-600 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                placeholder="Enter the question..."
              />
            </div>

            <div>
              <label className="block text-gray-400 text-sm mb-1">Category *</label>
              <select
                name="categoryId"
                value={formData.categoryId}
                onChange={handleInputChange}
                required
                className="w-full px-4 py-2 bg-gray-700 text-white border border-gray-600 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
              >
                <option value="">Select a category</option>
                {categories.map(cat => (
                  <option key={cat.categoryId} value={cat.categoryId}>
                    {cat.categoryName}
                  </option>
                ))}
              </select>
            </div>

            <div>
              <label className="block text-gray-400 text-sm mb-1">Template (Code Template)</label>
              <textarea
                name="template"
                value={formData.template}
                onChange={handleInputChange}
                rows={6}
                className="w-full px-4 py-2 bg-gray-700 text-white border border-gray-600 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 font-mono text-sm"
                placeholder="int functionName(param1, param2) { \n    // Write your code here\n}"
              />
            </div>

            <div>
              <label className="block text-gray-400 text-sm mb-1">Answer (Expected Solution)</label>
              <textarea
                name="answer"
                value={formData.answer}
                onChange={handleInputChange}
                rows={6}
                className="w-full px-4 py-2 bg-gray-700 text-white border border-gray-600 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 font-mono text-sm"
                placeholder="The expected answer/solution code..."
              />
            </div>

            <div>
              <label className="block text-gray-400 text-sm mb-1">Statements</label>
              <textarea
                name="statements"
                value={formData.statements}
                onChange={handleInputChange}
                rows={3}
                className="w-full px-4 py-2 bg-gray-700 text-white border border-gray-600 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                placeholder="Additional statements..."
              />
            </div>

            <div>
              <label className="block text-gray-400 text-sm mb-1">Connection Scopes (comma-separated)</label>
              <input
                type="text"
                name="connectionScopes"
                value={formData.connectionScopes}
                onChange={handleInputChange}
                className="w-full px-4 py-2 bg-gray-700 text-white border border-gray-600 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                placeholder="scope1, scope2, scope3"
              />
            </div>
          </div>
        </div>

        {/* Test Cases */}
        <div className="bg-gray-800 rounded-lg border border-gray-700 p-6">
          <div className="flex justify-between items-center mb-4">
            <h2 className="text-lg font-semibold text-white">Test Cases</h2>
            <button
              type="button"
              onClick={addTestCase}
              className="px-3 py-1 bg-green-600 text-white rounded text-sm hover:bg-green-700"
            >
              + Add Test Case
            </button>
          </div>

          {testCases.length === 0 ? (
            <p className="text-gray-400 text-sm">No test cases added yet.</p>
          ) : (
            <div className="space-y-4">
              {testCases.map((tc, index) => (
                <div key={index} className="bg-gray-700/50 rounded-lg p-4 border border-gray-600">
                  <div className="flex justify-between items-center mb-3">
                    <span className="text-gray-300 font-medium">Test Case #{index + 1}</span>
                    <button
                      type="button"
                      onClick={() => removeTestCase(index)}
                      className="px-2 py-1 bg-red-600 text-white rounded text-xs hover:bg-red-700"
                    >
                      Remove
                    </button>
                  </div>

                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                      <label className="block text-gray-400 text-xs mb-1">Input Value</label>
                      <input
                        type="text"
                        value={tc.inputValue || ''}
                        onChange={(e) => handleTestCaseChange(index, 'inputValue', e.target.value)}
                        className="w-full px-3 py-2 bg-gray-700 text-white border border-gray-600 rounded focus:outline-none focus:ring-2 focus:ring-blue-500 text-sm"
                        placeholder='{"param1": "value1"}'
                      />
                    </div>

                    <div>
                      <label className="block text-gray-400 text-xs mb-1">Output Type</label>
                      <select
                        value={tc.outputType || 'string'}
                        onChange={(e) => handleTestCaseChange(index, 'outputType', e.target.value)}
                        className="w-full px-3 py-2 bg-gray-700 text-white border border-gray-600 rounded focus:outline-none focus:ring-2 focus:ring-blue-500 text-sm"
                      >
                        <option value="string">String</option>
                        <option value="int">Integer</option>
                        <option value="float">Float</option>
                        <option value="boolean">Boolean</option>
                        <option value="list">List</option>
                        <option value="map">Map</option>
                      </select>
                    </div>

                    <div>
                      <label className="block text-gray-400 text-xs mb-1">Expected Output</label>
                      <input
                        type="text"
                        value={tc.outputValue || ''}
                        onChange={(e) => handleTestCaseChange(index, 'outputValue', e.target.value)}
                        className="w-full px-3 py-2 bg-gray-700 text-white border border-gray-600 rounded focus:outline-none focus:ring-2 focus:ring-blue-500 text-sm"
                        placeholder="Expected output value"
                      />
                    </div>

                    <div className="flex items-center gap-4">
                      <label className="flex items-center gap-2 text-gray-400 text-sm">
                        <input
                          type="checkbox"
                          checked={tc.hidden || false}
                          onChange={(e) => handleTestCaseChange(index, 'hidden', e.target.checked)}
                          className="w-4 h-4 rounded"
                        />
                        Hidden
                      </label>
                    </div>

                    <div className="md:col-span-2">
                      <label className="block text-gray-400 text-xs mb-1">Explanation</label>
                      <input
                        type="text"
                        value={tc.explanation || ''}
                        onChange={(e) => handleTestCaseChange(index, 'explanation', e.target.value)}
                        className="w-full px-3 py-2 bg-gray-700 text-white border border-gray-600 rounded focus:outline-none focus:ring-2 focus:ring-blue-500 text-sm"
                        placeholder="Explanation for this test case"
                      />
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Submit Button */}
        <div className="flex gap-4 justify-end">
          <button
            type="button"
            onClick={() => navigate('/admin/questions')}
            className="px-6 py-2 bg-gray-600 text-white rounded-lg hover:bg-gray-500"
          >
            Cancel
          </button>
          <button
            type="submit"
            disabled={saving}
            className="px-6 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50 flex items-center gap-2"
          >
            {saving ? (
              <>
                <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
                Saving...
              </>
            ) : (
              isEditMode ? 'Update Question' : 'Create Question'
            )}
          </button>
        </div>
      </form>
    </div>
  );
}

export default AdminQuestionForm;
